import { Box, Container, Typography, Stack, Grid, Paper } from '@mui/material';
import { Link } from 'react-router-dom';

const values = [
  {
    icon: '🇿🇦',
    title: 'Built for South Africa',
    description: 'We understand SA labour law because we live it. Every feature is designed around LRA, BCEA, EE Act, OHS Act, and POPIA requirements.',
  },
  {
    icon: '⚡',
    title: 'Compliance First',
    description: 'Automated alerts, audit trails, and regulatory reporting that keep you ahead of deadlines. Never miss a submission again.',
  },
  {
    icon: '🤝',
    title: 'People Centred',
    description: 'HR is about people, not paperwork. We handle the compliance so you can focus on building great teams.',
  },
  {
    icon: '🔒',
    title: 'Secure & POPIA Ready',
    description: 'End-to-end encryption, role-based access, and full POPIA compliance. Your employee data stays safe and sovereign.',
  },
];

const stats = [
  { value: '1,200+', label: 'Companies' },
  { value: '50,000+', label: 'Employees Managed' },
  { value: '99.9%', label: 'Uptime' },
  { value: '6', label: 'SA Regulations Covered' },
];

export function AboutPage() {
  return (
    <Box sx={{ bgcolor: '#050510' }}>
      {/* Hero with space background */}
      <Box sx={{ py: { xs: 8, md: 12 }, position: 'relative', overflow: 'hidden', minHeight: { xs: 400, md: 500 } }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=1920&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.25,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `
              radial-gradient(ellipse 60% 50% at 30% 50%, rgba(26,76,216,0.3) 0%, transparent 60%),
              linear-gradient(180deg, rgba(5,5,16,0.2) 0%, rgba(5,5,16,1) 100%)
            `,
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
          <Stack spacing={3} sx={{ maxWidth: 700 }}>
            <Typography sx={{ color: '#6b7a99', fontSize: 14, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              About Us
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: 36, md: 56 },
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: '-0.03em',
                color: '#f0f0f5',
              }}
            >
              HR Compliance,{' '}
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(135deg, #3d7aff 0%, #c9b107 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Simplified
              </Box>
            </Typography>
            <Typography sx={{ color: '#a0a5b5', fontSize: { xs: 16, md: 20 }, lineHeight: 1.7, maxWidth: 560 }}>
              Rosta was built to solve a real problem: South African businesses spend too much time
              navigating complex labour legislation instead of focusing on their people. We automated
              the compliance so you don't have to.
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* Stats */}
      <Box sx={{ py: 6, borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat) => (
              <Grid size={{ xs: 6, md: 3 }} key={stat.label}>
                <Stack alignItems="center" spacing={1}>
                  <Typography sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 800, color: '#f0f0f5' }}>
                    {stat.value}
                  </Typography>
                  <Typography sx={{ color: '#a0a5b5', fontSize: 14, fontWeight: 500 }}>
                    {stat.label}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Mission with image */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={3}>
                <Typography sx={{ color: '#6b7a99', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Our Mission
                </Typography>
                <Typography
                  variant="h2"
                  sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 700, color: '#f0f0f5', lineHeight: 1.2, letterSpacing: '-0.02em' }}
                >
                  Make every South African business compliant by default
                </Typography>
                <Typography sx={{ color: '#a0a5b5', fontSize: 17, lineHeight: 1.8 }}>
                  We believe compliance shouldn't be a burden. It should be automatic. Rosta
                  transforms the complex web of SA labour legislation — LRA, BCEA, EE Act, OHS Act,
                  POPIA, FICA — into simple, automated workflows that keep your business protected
                  without the paperwork.
                </Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 3,
                  overflow: 'hidden',
                  aspectRatio: '4/3',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&q=80"
                  alt="Aurora borealis — representing the dynamic energy of South African workplaces"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(135deg, rgba(26,76,216,0.2) 0%, rgba(5,5,16,0.4) 100%)',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Values */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#0a0a18' }}>
        <Container maxWidth="lg">
          <Stack alignItems="center" spacing={2} sx={{ mb: 8 }}>
            <Typography sx={{ color: '#6b7a99', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Our Values
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 700, color: '#f0f0f5', textAlign: 'center' }}>
              What Drives Us
            </Typography>
          </Stack>
          <Grid container spacing={4}>
            {values.map((value) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={value.title}>
                <Paper
                  sx={{
                    p: 4,
                    height: '100%',
                    bgcolor: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: 'rgba(61,122,255,0.3)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Stack spacing={2}>
                    <Typography sx={{ fontSize: 32 }}>{value.icon}</Typography>
                    <Typography sx={{ fontWeight: 600, color: '#f0f0f5', fontSize: 16 }}>
                      {value.title}
                    </Typography>
                    <Typography sx={{ color: '#a0a5b5', fontSize: 14, lineHeight: 1.7 }}>
                      {value.description}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* SA Compliance */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Stack alignItems="center" spacing={2} sx={{ mb: 8 }}>
            <Typography sx={{ color: '#6b7a99', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Regulatory Coverage
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 700, color: '#f0f0f5', textAlign: 'center' }}>
              Fully Compliant with SA Law
            </Typography>
            <Typography sx={{ color: '#a0a5b5', fontSize: 17, textAlign: 'center', maxWidth: 600 }}>
              Every module in Rosta is built against the specific sections and schedules of South African
              labour legislation.
            </Typography>
          </Stack>
          <Grid container spacing={3}>
            {[
              { code: 'LRA', name: 'Labour Relations Act', detail: 'Disciplinary procedures (Schedule 8), retrenchments (Section 189), CCMA referrals' },
              { code: 'BCEA', name: 'Basic Conditions of Employment Act', detail: 'Employment contracts, leave calculations, working hours, Section 42 certificates' },
              { code: 'EE Act', name: 'Employment Equity Act', detail: 'EEA2/EEA4 reports, designated groups, annual submissions to DEL' },
              { code: 'OHS Act', name: 'Occupational Health & Safety', detail: 'Workplace injuries, Annexure 1, certifications, OHS representative management' },
              { code: 'POPIA', name: 'Protection of Personal Information', detail: 'Data protection, retention schedules, consent management, deletion queues' },
              { code: 'FICA', name: 'Financial Intelligence Centre Act', detail: 'Suspicious transaction reporting, client verification, record keeping' },
            ].map((reg) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={reg.code}>
                <Box
                  sx={{
                    p: 4,
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.06)',
                    bgcolor: 'rgba(255,255,255,0.02)',
                    height: '100%',
                    transition: 'all 0.3s',
                    '&:hover': { borderColor: 'rgba(61,122,255,0.3)' },
                  }}
                >
                  <Stack spacing={1.5}>
                    <Box
                      sx={{
                        display: 'inline-block',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: 'rgba(61,122,255,0.1)',
                        color: '#3d7aff',
                        fontSize: 13,
                        fontWeight: 700,
                        width: 'fit-content',
                      }}
                    >
                      {reg.code}
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: '#f0f0f5', fontSize: 15 }}>
                      {reg.name}
                    </Typography>
                    <Typography sx={{ color: '#a0a5b5', fontSize: 13, lineHeight: 1.6 }}>
                      {reg.detail}
                    </Typography>
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: { xs: 8, md: 12 }, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Stack spacing={3} alignItems="center">
            <Typography variant="h2" sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 700, color: '#f0f0f5' }}>
              Ready to Get Started?
            </Typography>
            <Typography sx={{ color: '#a0a5b5', fontSize: 17, maxWidth: 500 }}>
              Join 1,200+ South African companies automating their HR compliance.
            </Typography>
            <Box
              component={Link}
              to="/signup"
              sx={{
                display: 'inline-block',
                px: 4,
                py: 1.5,
                borderRadius: '9999px',
                background: 'linear-gradient(135deg, #1a4cd8, #3d7aff)',
                color: '#fff',
                fontWeight: 600,
                fontSize: 16,
                textDecoration: 'none',
                '&:hover': { background: 'linear-gradient(135deg, #3d7aff, #1a4cd8)', transform: 'translateY(-1px)' },
                transition: 'all 0.2s',
              }}
            >
              Start Free Trial →
            </Box>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
